import debounce from 'lodash/debounce';
import React, { FC, RefObject, createRef, useCallback, useEffect, useRef, useState } from 'react';
import { ApiProfile } from '../../api/auth';
import styles from './styles.module.scss';
import Avatar from '../../components/Avatar';
import { Button, Form } from '../../kit';
import { c } from '../../lib/util';
import { faFilePdf, faImage, faVideo } from '@fortawesome/pro-regular-svg-icons';
import Loader from '../../components/Loader';
import { ApiPost, ApiPostFile } from '../../models/Post/api';
import { PostApi } from '../../models/Post';
import { useDebounceSearch } from '../../lib/hooks';
import { CoachApi } from '../../models/Coach';

interface CreatePostProps {
    auth: ApiProfile,
    edit?: ApiPost,
    onPost: (post: ApiPost) => void,
    parent?: ApiPost,
    profileId?: number
}

const CreatePost: FC<CreatePostProps> = ({ auth, edit, onPost, parent, profileId }): JSX.Element => {
    const [ isActive, setIsActive ] = useState<boolean>(false);
    const [ isLoading, setIsLoading ] = useState<boolean>(false);
    const [ content, setContent ] = useState<string>('');
    const [ files, setFiles ] = useState<Partial<ApiPostFile>[]>([]);
    const [ deleteFiles, setDeleteFiles ] = useState<number[]>([]);
    const [ mentions, setMentions ] = useState<ApiProfile[]>([]);
    const [ youTube, setYouTube ] = useState<string>();
    const [ vimeo, setVimeo ] = useState<string>();
    const fileInputRef: RefObject<HTMLInputElement> = createRef();
    const isMentioning = useRef<boolean>(false);
    const debounceMention = useDebounceSearch((mention) => {
        if (isMentioning.current) return;
        isMentioning.current = true;
        CoachApi.list({ query: mention.substring(1), limit: 3 }).then(({ data }) => {
            setMentions(data.data);
            isMentioning.current = false;
        });
    }, 500);

    useEffect(() => {
        if (!edit) return;
        setContent(edit.content);
        setIsActive(true);
        document.getElementById(`focus-me-${parent?.id || ''}`)?.focus();
    }, [edit]);

    useEffect(() => {
        const regExp = /((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([a-zA-Z0-9\-]+)/;
        const match = content.match(regExp);
        setYouTube(match && match[7].length === 11 ? match[7] : undefined);

        const v = content.match(/https:\/\/vimeo\.com\/([0-9]+)/);
        setVimeo(v && v[1] ? v[1] : undefined);

        // mention
        const mention = content.match(/(@[a-zA-Z]+)$/);
        if (mention && mention[0]) {
            debounceMention(mention[0]);
        } else {
            setMentions([]);
        }
    }, [content]);

    const handleFocus = useCallback((): void => {
        if (isActive) return;
        setIsActive(true);
        document.getElementById(`focus-me-${parent?.id || ''}`)?.focus();
    }, [isActive]);

    useEffect(() => {
        window.addEventListener('focus-post', handleFocus);
        return () => window.removeEventListener('focus-post', handleFocus);
    }, [handleFocus]);

    const selectFiles = (): void => {
        if (!fileInputRef.current) return;
        setIsActive(true);
        fileInputRef.current.click();
    }

    const handleFiles = (fileList: FileList | null): void => {
        if (!fileList) return;
        const newFiles = [ ...files ];
        setIsActive(true);
        Array.from(fileList).forEach((file) => {
            if (file.type.includes('image')) {
                const reader = new FileReader();
                reader.onload = () => {
                    setFiles((f) => ([
                        ...f,
                        {
                            name: file.name,
                            file,
                            type: 'image',
                            preview: reader.result as string,
                        }
                    ]));
                };
                reader.readAsDataURL(file);
            } else {
                newFiles.push({
                    name: file.name,
                    file,
                    type: file.type.includes('pdf') ? 'document' : 'video',
                });
                setFiles(newFiles);
            }
        });
    }

    const save = (): void => {
        setIsLoading(true);
        PostApi.store(content, files, profileId, parent?.id).then(({ data }) => {
            onPost(data.data);
            setYouTube(undefined);
            setFiles([]);
            setContent('');
            setIsLoading(false);
            setIsActive(false);
        }).catch(() => {
            setIsLoading(false);
            (window as any).alert('Vul een bericht in');
        });
    }

    const update = (): void => {
        if (!edit) return;
        setIsLoading(true);
        PostApi.update({
            ...edit,
            files: edit.files.filter((o) => !deleteFiles.includes(o.id)),
        }, content, files).then(({ data }) => {
            onPost(data.data);
            setYouTube(undefined);
            setFiles([]);
            setContent('');
            setIsLoading(false);
            setIsActive(false);
        }).catch(() => {
            setIsLoading(false);
            (window as any).alert('Vul een bericht in');
        });
    }

    const handleBlur = (): void => {
        if (content === '' && files.length <= 0) {
            setIsActive(false);
        }
    }

    const handleMention = (mention: ApiProfile): void => {
        setContent((c) => c.replace(/(@[a-zA-Z]+)$/, `@${mention.name.replace(/ /g, '-').replace('\'', '').toLowerCase()}`));
    }

    return (
        <div
            className={c([styles.create, isActive && styles.isActive, !parent && styles.line])}
            onClick={() => handleFocus()}
        >
            {isLoading && (
                <div className={styles.loader}>
                    <Loader />
                </div>
            )}
            <div className={styles.col}>
                <Avatar profile={auth} size={48} />
                <div className={styles.createOptions}>
                    <input
                        ref={fileInputRef}
                        accept="image/*,video/mp4,application/pdf"
                        multiple
                        onChange={(e) => handleFiles(e.target.files)}
                        type="file"
                        style={{ display: 'none' }}
                    />
                    <Button
                        color="border"
                        icon={faImage}
                        tooltip="Voeg foto('s) toe"
                        onClick={() => selectFiles()}
                    />
                    <Button
                        color="border"
                        icon={faFilePdf}
                        tooltip="Voeg document(en) toe"
                        onClick={() => selectFiles()}
                    />
                    <Button
                        color="border"
                        icon={faVideo}
                        tooltip="Voeg video('s) toe"
                        onClick={() => selectFiles()}
                    />
                </div>
            </div>
            <div className={styles.createContent}>
                <Form.Textarea
                    id={`focus-me-${parent?.id || ''}`}
                    onBlur={() => handleBlur()}
                    onChange={({ value }: any) => setContent(value)}
                    placeholder={isActive
                        ? 'Waar wil je het over hebben?'
                        : (parent ? 'Reageren' : 'Start hier met je bijdrage')}
                    value={content}
                />
                {mentions.length > 0 && (
                    <div className={styles.mentions}>
                        {mentions.map((m) => (
                            <div
                                key={`ment-${m.id}`}
                                className={styles.mention}
                                onClick={() => handleMention(m)}
                            >
                                <Avatar profile={m} size={24} />
                                {m.name}
                            </div>
                        ))}
                    </div>
                )}
                {youTube && (
                    <div className="ratio ratio-16x9">
                        <iframe src={`https://www.youtube.com/embed/${youTube}`} />
                    </div>
                )}
                {vimeo && (
                    <div className="ratio ratio-16x9">
                        <iframe src={`https://player.vimeo.com/video/${vimeo}?byline=0&portrait=0`} />
                    </div>
                )}
                {(files.length > 0 || content !== '' || edit?.files) && (
                    <div className={styles.buttons}>
                        <div className={styles.attachments}>
                            {files.map((file, index) => (
                                <div
                                    key={`attachment-${index}`}
                                    className={styles.attachment}
                                    onClick={() => setFiles((f) => f.filter((o, i) => i !== index))}
                                >
                                    {file.type === 'video' && <i className="far fa-video" />}
                                    {file.type === 'document' && <i className="far fa-file-pdf" />}
                                    {file.type === 'image' && (
                                        <div style={{ backgroundImage: `url(${file.preview})` }} />
                                    )}
                                </div>
                            ))}
                            {edit && edit.files.filter((o) => !deleteFiles.includes(o.id)).map((file, index) => (
                                <div
                                    key={`attachment-${index}`}
                                    className={styles.attachment}
                                    onClick={() => setDeleteFiles([...deleteFiles, file.id])}
                                >
                                    {file.type === 'video' && <i className="far fa-video" />}
                                    {file.type === 'document' && <i className="far fa-file-pdf" />}
                                    {file.type === 'image' && (
                                        <div style={{ backgroundImage: `url(${file.preview})` }} />
                                    )}
                                </div>
                            ))}
                        </div>
                        {(content !== '' || files.length > 0) && (
                            <Button
                                color="primary"
                                label={`${parent ? 'Reactie' : 'Bericht'} ${edit ? 'wijzigen' : 'plaatsen'}`}
                                onClick={edit ? () => update() : () => save()}
                                style={{ marginBottom: 16 }}
                            />
                        )}
                    </div>
                )}
            </div>
            <div className={styles.previewOptions}>
                <Button
                    color="border"
                    icon={faImage}
                    tooltip="Voeg foto('s) toe"
                    onClick={() => selectFiles()}
                />
                <Button
                    color="border"
                    icon={faFilePdf}
                    tooltip="Voeg document(en) toe"
                    onClick={() => selectFiles()}
                />
                <Button
                    color="border"
                    icon={faVideo}
                    tooltip="Voeg video('s) toe"
                    onClick={() => selectFiles()}
                />
            </div>
        </div>
    );
}

export default CreatePost;
